import { useEffect, useRef } from 'react';
import { Modal } from 'antd';
import { useBlocker } from 'react-router-dom';
import { useHoldUnsavedWork } from './practiceSwitch';

// One exit guard for an editor holding unsaved work (studio-foundation review, 4 Nov 2026). Every way out asks first:
// the drawer's close button, Escape and the mask (through `requestClose`), leaving the page (the rail, the back button,
// a link), switching practice, and closing or reloading the browser tab. Each choice is Keep editing or Discard; nothing
// is lost without one. Promoted from the module editor, its first user, so a second editor never writes its own copy.

/**
 * Guards `onClose` while `dirty`. Returns the close to give the drawer's `onClose` and its Cancel button.
 * `label` names the work in the questions, e.g. "this module" or "scenario folfox-c2-fever".
 */
export function useUnsavedExit(dirty: boolean, label: string, onClose: () => void): () => void {
  useHoldUnsavedWork(dirty, label);

  // Leaving the page: a route change to another path asks, once per attempt. The answer acts on the blocker as it is
  // when the person answers: a blocker captured when the question opened may have moved on (a second navigation while
  // blocked re-blocks with a new object), and proceeding a blocker that is no longer blocked throws.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);
  const latest = useRef(blocker);
  latest.current = blocker;
  const asking = useRef(false);
  useEffect(() => {
    if (blocker.state !== 'blocked' || asking.current) return;
    asking.current = true;
    const answer = (leave: boolean): void => {
      asking.current = false;
      const now = latest.current;
      if (now.state !== 'blocked') return;
      if (leave) now.proceed();
      else now.reset();
    };
    Modal.confirm({
      title: 'Leave without saving?',
      content: `Your changes to ${label} are not saved.`,
      okText: 'Discard and leave',
      okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: () => answer(true),
      onCancel: () => answer(false),
    });
  }, [blocker, label]);

  // Closing or reloading the tab: the browser asks with its own words.
  useEffect(() => {
    if (!dirty) return undefined;
    const ask = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [dirty]);

  return () => {
    if (!dirty) {
      onClose();
      return;
    }
    Modal.confirm({
      title: 'Discard changes?',
      content: `Your changes to ${label} are not saved.`,
      okText: 'Discard',
      okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: onClose,
    });
  };
}
